import { useEffect, useState, useSyncExternalStore } from 'react'
import {
  LOD_RANK,
  expandRect,
  pickLodLevel,
  rectsIntersect,
  type LodLevel,
  type MediaDto,
  type Rect,
} from '@praesto/shared'
import type { CameraStore } from './CameraStore.js'

/** Retina jenseits von 2× lohnt den Bandbreitenaufwand nicht. */
const MAX_DPR = 2
/** Rand um den Viewport, in dem schon geladen wird (20 %). */
const PRELOAD_MARGIN = 1.2

/**
 * Ob ein Element gerendert werden soll (Culling).
 *
 * Läuft über `useSyncExternalStore`: der Snapshot ist ein Boolean, ein
 * Kamera-Update ohne Sichtbarkeitswechsel löst also keinen Render aus.
 */
export function useIsVisible(camera: CameraStore, rect: Rect): boolean {
  return useSyncExternalStore(camera.subscribe, () =>
    rectsIntersect(expandRect(camera.getViewport(), PRELOAD_MARGIN), rect),
  )
}

/** Auflösungsstufe, die die aktuelle Bildschirmgröße dieses Elements verlangt. */
export function useRequiredLevel(
  camera: CameraStore,
  widthOnBoard: number,
  media: MediaDto | undefined,
): LodLevel {
  return useSyncExternalStore(camera.subscribe, () => {
    if (!media) return 'thumb'
    const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR)
    return pickLodLevel(widthOnBoard * camera.getCamera().zoom * dpr, media.variants)
  })
}

export interface LoadedImage {
  level: LodLevel
  url: string
}

/**
 * Lädt die passende Stufe nach und tauscht sie erst, wenn sie dekodiert ist.
 *
 * Zwei Regeln, die über "flüssig" vs. "flackert" entscheiden:
 *  1. Nie herunterstufen — ein bereits dekodiertes Bild gegen ein kleineres zu
 *     tauschen kostet Arbeit und bringt sichtbar nichts.
 *  2. Erst tauschen, wenn das neue Bild fertig ist, sonst blitzt beim Zoomen
 *     kurz ein leeres Element auf.
 */
export function useAdaptiveImage(
  media: MediaDto | undefined,
  requiredLevel: LodLevel,
  active: boolean,
): LoadedImage | null {
  const [loaded, setLoaded] = useState<LoadedImage | null>(null)

  useEffect(() => {
    if (!active || !media || media.variants.length === 0) return

    const target =
      media.variants.find((variant) => variant.level === requiredLevel) ??
      media.variants[media.variants.length - 1]
    if (!target) return
    if (loaded && LOD_RANK[loaded.level] >= LOD_RANK[target.level]) return

    let cancelled = false
    const image = new Image()
    image.decoding = 'async'
    image.src = target.url

    const commit = (): void => {
      if (!cancelled) setLoaded({ level: target.level, url: target.url })
    }
    // `decode()` liefert ein fertig dekodiertes Bild; der Tausch verursacht dann
    // keinen Ruckler mehr im Renderpfad.
    image.decode().then(commit, () => {
      image.onload = commit
    })

    return () => {
      cancelled = true
      image.onload = null
    }
  }, [media, requiredLevel, active, loaded])

  return loaded
}
